// A harang FEED-je — a forrás-igazság. KEYSET-CURSOR lapozás (`nextCursor` → `pageParam`), a lista-oldalak
// mintája szerint (`routes/activity-log.tsx`), csak kisebb lappal: a panelben „továbbiak" gomb lapoz.
//
// A feed EGY query az egész appra: a badge (olvasatlan-szám) és a panel-lista UGYANEBBŐL él, így nincs
// két-forrás-igazság. Az SSE-jelzés és a mutációk ezt a kulcsot invalidálják.
import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  NOTIFICATION_PAGE_SIZE,
  notificationKeys,
  rowsOf,
  unreadCountOf,
  type NotificationFeed,
  type NotificationFeedPage,
  type NotificationRow,
} from '../api';

import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

export interface NotificationFeedState {
  rows: NotificationRow[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
  query: UseInfiniteQueryResult<NotificationFeed>;
}

/**
 * A bejelentkezett user SAJÁT értesítés-feedje. Nincs jog-gate (a feed a címzettre szűrt — az Odin RLS a fal);
 * a query CSAK bejelentkezve fut (token nélkül nincs mit kérdezni).
 */
export function useNotificationFeed(): NotificationFeedState {
  const hasToken = useAuthStore((s) => s.token !== null);

  const query = useInfiniteQuery({
    queryKey: notificationKeys.feed({ unreadOnly: false }),
    enabled: hasToken,
    queryFn: ({ pageParam }) =>
      api.notifications.query({
        query: { limit: NOTIFICATION_PAGE_SIZE, cursor: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: NotificationFeedPage) =>
      lastPage.status === 200 ? (lastPage.body.nextCursor ?? undefined) : undefined,
  });

  const rows = useMemo(() => rowsOf(query.data), [query.data]);
  const unreadCount = unreadCountOf(query.data);
  // Egyetlen sikeres lap sincs (pl. 401 után) → hiba-állapot (a panel a hiba-szöveget mutatja).
  const pages = query.data?.pages ?? [];
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);

  return {
    rows,
    unreadCount,
    isLoading: query.isPending && hasToken,
    isError: query.isError || hasNoSuccess,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    query,
  };
}
